import { useEffect, useMemo, useState } from 'react'

import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  Row,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table'

import { FiltersRow } from '@/components/new/table/filter/FiltersRow'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { HeaderActions } from './header/HeaderActions'
import { Navigation } from './Navigation'
import { useTableContext } from './TableContext'

// DOCS: https://ui.shadcn.com/docs/components/data-table#basic-table

interface DataTableProps<TData, TValue> {
  columns: TableColumn<TData, TValue>[]
  storageKey: string
}

interface ColumnsAditionalProps {
  skeletonWidth?: string //Default value: '80%'
  defaultVisibilityMobile?: boolean // Default value: true
}

export type TableColumn<TData = unknown, TValue = unknown> = ColumnDef<
  TData,
  TValue
> &
  ColumnsAditionalProps

export function DataTable<TData, TValue>({
  columns,
  storageKey,
}: DataTableProps<TData, TValue>) {
  // Use the table context
  const {
    paginate,
    data,
    isLoading,
    skeletonRowCount,
    onRowClick,
    quickFilterList,
    search,
    leftButtons,
    rightButtons,
  } = useTableContext()

  const { currentPage } = paginate || {}

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [isMobileView, setIsMobileView] = useState(false)

  // Create skeleton columns when loading
  const skeletonColumns = useMemo(() => {
    if (!isLoading) return columns

    return columns.map((column) => {
      // Different width based on column type
      const width = column.skeletonWidth ?? '80%'
      const height = '20px'

      return {
        ...column,
        cell: () => (
          <div className="flex items-center">
            <Skeleton className={`h-${height} w-${width}`} />
          </div>
        ),
      }
    })
  }, [columns, isLoading])

  // Create skeleton data for loading state
  const skeletonData = useMemo(() => {
    if (!isLoading) return []

    return Array.from({ length: paginate?.pageSize ?? data.length }).map(
      (_, index) => ({
        id: `skeleton-${index}`,
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, skeletonRowCount])

  // Create a memoized version of the data to ensure stable references
  const tableData = useMemo(() => {
    if (isLoading) {
      return skeletonData as TData[]
    }
    return (data as TData[]) || []
  }, [data, isLoading, skeletonData])

  // Check for mobile view on mount and window resize
  useEffect(() => {
    const checkViewport = () => {
      setIsMobileView(window.innerWidth < 768)
    }

    checkViewport()
    window.addEventListener('resize', checkViewport)

    return () => {
      window.removeEventListener('resize', checkViewport)
    }
  }, [])

  // Set default column visibility based on screen size
  useEffect(() => {
    if (isMobileView) {
      // On mobile, show only high-priority columns
      const mobileVisibility: VisibilityState = {}
      columns.forEach((column) => {
        if (column.id) {
          mobileVisibility[column.id] = column.defaultVisibilityMobile ?? true
        }
      })
      setColumnVisibility((prev) => ({ ...prev, ...mobileVisibility }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileView])

  // Load column visibility settings from localStorage
  useEffect(() => {
    try {
      const storedColumns = localStorage.getItem(storageKey)
      if (storedColumns) {
        const parsedColumns = JSON.parse(storedColumns)
        // Validate that the parsed data is an object
        if (parsedColumns && typeof parsedColumns === 'object') {
          setColumnVisibility(parsedColumns)
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load column visibility settings:', error)
      // Continue with default column visibility on error
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Handle column visibility changes - both update state and save to localStorage
  const handleColumnVisibilityChange = (
    updatedVisibility:
      | VisibilityState
      | ((_prev: VisibilityState) => VisibilityState)
  ) => {
    // First update the state
    setColumnVisibility(updatedVisibility)

    // Then save to localStorage
    try {
      // If it's a function, we need to get the actual value
      const newVisibility =
        typeof updatedVisibility === 'function'
          ? updatedVisibility(columnVisibility)
          : updatedVisibility

      if (Object.keys(newVisibility).length > 0) {
        localStorage.setItem(storageKey, JSON.stringify(newVisibility))
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save column visibility settings:', error)
      // Continue without saving settings on error
    }
  }

  // Reset table when the page changes by recreating the table instance
  const table = useReactTable({
    data: tableData,
    columns: isLoading
      ? (skeletonColumns as ColumnDef<TData, unknown>[])
      : columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: handleColumnVisibilityChange,
    state: {
      columnVisibility,
    },
    // This key ensures the table is reset when data changes
    manualPagination: true,
  })
  // Access columns if needed via table.getAllColumns(); no side effects required here.

  // Card view for mobile rendering
  const renderMobileCard = (row: Row<TData>) => {
    // For skeleton rows
    if (isLoading) {
      return (
        <div
          aria-rowindex={row.index + 2}
          className="mb-4 rounded-md border bg-card p-4 shadow-sm"
          key={row.id}
          role="row"
        >
          <div className="grid grid-cols-2 gap-2">
            {table.getVisibleLeafColumns().map((column) => (
              <div className="mb-2" key={column.id} role="cell">
                <div className="text-xs font-medium text-muted-foreground">
                  {typeof column.columnDef.header === 'string'
                    ? column.columnDef.header
                    : column.id}
                </div>
                <div className="flex h-5 items-center">
                  <Skeleton className="h-4 w-4/5" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    return (
      <div
        aria-rowindex={row.index + 2} // +2 for header row and 0-indexing
        className={[
          'mb-4 rounded-md border bg-card p-4 shadow-sm',
          onRowClick ? 'cursor-pointer hover:bg-accent/50' : '',
        ].join(' ')}
        key={row.id}
        onClick={(e) => onRowClick && onRowClick(row, e)}
        role="row"
      >
        <div className="grid grid-cols-2 gap-2">
          {row.getVisibleCells().map((cell) => {
            // Skip rendering for shipping when it's falsy (mobile card view)
            if (cell.column.id === 'shipping') {
              const original = row.original as Record<string, unknown>
              const shipping = (original as Record<string, unknown>)['shipping']
              if (!shipping) return null
            }

            const header =
              typeof cell.column.columnDef.header === 'string'
                ? cell.column.columnDef.header
                : cell.column.id

            return (
              <div className="mb-2" key={cell.id} role="cell">
                <div className="text-xs font-medium text-muted-foreground">
                  {header}
                </div>
                <div>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }
  return (
    <>
      {(!!quickFilterList || !!search) && <FiltersRow />}
      <div className="rounded-lg border bg-card">
        {(!!leftButtons || !!rightButtons || !!paginate) && (
          <HeaderActions
            columns={
              table.getAllColumns() as unknown as Column<TData, TValue>[]
            }
          />
        )}
        <div className="border-t">
          <div>
            {isLoading || table.getRowModel().rows?.length ? (
              isMobileView ? (
                // Mobile card view
                <div aria-label="Tabla" className="p-4" role="table">
                  {table
                    .getHeaderGroups()
                    .some((item) =>
                      item.headers.some(
                        (header) => header.column.columnDef.header !== ''
                      )
                    ) && (
                    <div className="sr-only" role="rowgroup">
                      <div role="row">
                        {table.getHeaderGroups().map((headerGroup) =>
                          headerGroup.headers.map((header) => (
                            <div key={header.id} role="columnheader">
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  <div key={`page-${currentPage}`} role="rowgroup">
                    {table
                      .getRowModel()
                      .rows.map((row) => renderMobileCard(row))}
                  </div>
                </div>
              ) : (
                // Standard table view
                <div
                  aria-label="Tabla"
                  className="overflow-x-auto"
                  role="region"
                >
                  <Table>
                    {table
                      .getHeaderGroups()
                      .some((item) =>
                        item.headers.some(
                          (header) => header.column.columnDef.header !== ''
                        )
                      ) && (
                      <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                          <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                              return (
                                <TableHead key={header.id}>
                                  {header.isPlaceholder
                                    ? null
                                    : flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                      )}
                                </TableHead>
                              )
                            })}
                          </TableRow>
                        ))}
                      </TableHeader>
                    )}

                    <TableBody key={`page-${currentPage}`}>
                      {table.getRowModel().rows.map((row) => {
                        // For skeleton rows during loading
                        if (isLoading) {
                          return (
                            <TableRow className="h-11" key={row.id}>
                              {row.getVisibleCells().map((cell) => {
                                return (
                                  <TableCell
                                    className="px-0.5 py-1"
                                    key={cell.id}
                                  >
                                    <Skeleton className="h-10 w-full rounded-none bg-gray-100" />
                                  </TableCell>
                                )
                              })}
                            </TableRow>
                          )
                        }

                        return (
                          <TableRow
                            className={`${onRowClick ? 'cursor-pointer hover:bg-accent/50' : ''}`}
                            data-state={row.getIsSelected() && 'selected'}
                            key={row.id}
                            onClick={(e) => onRowClick && onRowClick(row, e)}
                          >
                            {row.getVisibleCells().map((cell) => (
                              <TableCell key={cell.id}>
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              )
            ) : (
              <div className="py-10 text-center">
                <p className="text-muted-foreground">No hay resultados.</p>
              </div>
            )}
            {paginate && <Navigation />}
          </div>
        </div>
      </div>
    </>
  )
}
